import { MyAwpVisitsDocument } from '@mdvip/shared/graphql/generated/graphql';
import {
  AwpDocument,
  AwpPriorityBiomarkersDocument,
  type AwpPriorityBiomarkersQuery,
  type AwpQuery,
} from '@mdvip/shared/graphql/labs/generated/graphql';
import { gqlRequest, labsGqlRequest } from '@mdvip/shared/lib/graphql-client';
import { trackEvent } from '@mdvip/shared/lib/telemetry';

import { isKnownBandLabel } from './model/labStatus';
import type { AwpPanel, ThemePriority } from './model/types';

/**
 * Every AWP query sits under `['awp']`, so one prefix reaches them all: the physician app shortens
 * how long lab data outlives its last viewer on `awpKeys.all`, and a future invalidation can too.
 */
export const awpKeys = {
  all: ['awp'] as const,
  visits: (memberId: string) => ['awp', 'visits', memberId] as const,
  panel: (memberId: string, appointmentDate: string) => ['awp', 'panel', memberId, appointmentDate] as const,
  priority: (memberId: string) => ['awp', 'priority', memberId] as const,
};

/**
 * How many visits before the current one are loaded for trends: the priority cards' "Last AWP" and
 * the all-labs grid's trend both read `previous[0]` only, but `previous[1]` tells them whether a gap
 * is a missed draw or the member simply has no older visit.
 */
export const HISTORY_DEPTH = 2;

/** Lab results change rarely, and each `awp` call costs labs-api a decrypt, so both queries stay fresh for 10 minutes. */
export const AWP_STALE_TIME = 10 * 60 * 1000;

/**
 * The visit dates to load, newest first: the current visit and `HISTORY_DEPTH` before it. labs-api can't
 * answer without a date, so this list decides which panels are requested.
 */
export async function requestAwpVisits(signal?: AbortSignal): Promise<string[]> {
  const data = await gqlRequest(MyAwpVisitsDocument, { mostRecent: 1 + HISTORY_DEPTH }, signal);
  return data.myAwpVisits.map((visit) => visit.appointmentDate);
}

/** One visit's labs, from labs-api. */
export async function requestAwpPanel(
  memberId: string,
  appointmentDate: string,
  signal?: AbortSignal,
): Promise<AwpPanel> {
  return toAwpPanel(await labsGqlRequest(AwpDocument, { memberId, appointmentDate }, signal));
}

/**
 * The member's priority biomarkers, one entry per theme. Unlike `awp`, it takes no date and
 * decrypts nothing, so it's fetched once per member rather than once per visit.
 */
export async function requestAwpPriority(memberId: string, signal?: AbortSignal): Promise<ThemePriority[]> {
  return toAwpPriority(await labsGqlRequest(AwpPriorityBiomarkersDocument, { memberId }, signal));
}

/**
 * The `awp` response → the panel `buildAwpDashboard` reads. A pass-through today, but this is the one
 * place a labs-api response change should be absorbed (a renamed field, a band tier enum, a LOINC on
 * undrawn rows), so the model and the panels built on it don't have to change with it.
 *
 * It also reports band labels the model won't recognise — once per label per response, so each fetch is
 * reported once however often the model is rebuilt from it. The label only, never a value.
 */
export function toAwpPanel(data: AwpQuery): AwpPanel {
  const panel = data.awp;
  for (const label of unknownBandLabels(panel)) trackEvent('awp_unknown_band_label', { label });
  return panel;
}

/** The `awpPriorityBiomarkers` response → the list `selectPriorityCards` reads. The absorption point for a labs-api response change, same role as `toAwpPanel`. */
export function toAwpPriority(data: AwpPriorityBiomarkersQuery): ThemePriority[] {
  return data.awpPriorityBiomarkers;
}

function unknownBandLabels(panel: AwpPanel): Set<string> {
  const labels = new Set<string>();
  for (const entry of panel.biomarkers) {
    if (entry.__typename !== 'Biomarker') continue;
    for (const band of entry.referenceBands ?? []) {
      if (!isKnownBandLabel(band.label)) labels.add(band.label);
    }
  }
  return labels;
}
